import { z } from 'zod';
import type { Valor, Valores, ValorVersion } from './strategy';

/* ─────────────────────────────────────────────────────────────────────
   Valores — o histórico das edições e a sugestão de detalhamento.

   O histórico é do servidor: quando um valor muda, a versão anterior vai
   para o histórico (as cinco últimas). Cada edição é uma sessão — aberta na
   caneta, ao aplicar a sugestão da IA ou ao restaurar uma versão —, e o
   salvamento automático dentro da mesma sessão conta como uma edição só,
   para o histórico não se encher de letras.
   ───────────────────────────────────────────────────────────────────── */

export const VALOR_HISTORY = 5;

const same = (a: Pick<Valor, 'name' | 'meaning'>, b: Pick<Valor, 'name' | 'meaning'>) =>
  a.name.trim() === b.name.trim() && a.meaning.trim() === b.meaning.trim();

/**
 * Junta o que chegou do navegador com o que está gravado. O histórico, o
 * autor e a data nunca vêm do navegador; só o nome, o detalhamento e se ele
 * veio da IA.
 */
export function mergeValores(stored: Valores | null, incoming: Valores, who: { name: string; now: Date }): Valores {
  const before = new Map((stored?.values ?? []).map(v => [v.id, v]));
  const now = who.now.toISOString();
  return {
    values: incoming.values.map(v => {
      const prev = v.id ? before.get(v.id) : undefined;
      if (!prev) return { ...v, at: now, by: who.name, history: [] };
      if (same(prev, v) && prev.ia === v.ia) return { ...v, at: prev.at, by: prev.by, ia: prev.ia, sess: prev.sess, history: prev.history };
      /* mesma sessão de edição: a versão de antes da sessão já está no histórico */
      const session = !!v.sess && v.sess === prev.sess;
      const version: ValorVersion = { name: prev.name, meaning: prev.meaning, at: prev.at, by: prev.by, ia: prev.ia };
      /* versão vazia, ou só com o nome (o detalhamento sendo escrito pela primeira vez), não vira histórico */
      const trivial = !prev.meaning.trim() && (!prev.name.trim() || prev.name.trim() === v.name.trim());
      const history = session || trivial ? prev.history : [version, ...prev.history].slice(0, VALOR_HISTORY);
      return { ...v, at: now, by: who.name, history };
    }),
  };
}

/** Uma sessão de edição nova (a caneta, a sugestão da IA, restaurar uma versão). */
export const newValorSession = () => Math.random().toString(36).slice(2, 12);

/* ── a sugestão de detalhamento ───────────────────────────────────── */

export const valoresSuggestRequestSchema = z.object({
  /** os valores a detalhar; vazio = todos os que têm nome */
  ids: z.array(z.string().max(48)).max(12).default([]),
});
export const valoresSuggestionSchema = z.object({
  items: z.array(z.object({
    id: z.string().min(1).max(48),
    meaning: z.string().trim().min(10).max(240),
  })).max(12),
});
export type ValoresSuggestion = z.infer<typeof valoresSuggestionSchema>;
export interface ValoresSuggestionDto extends ValoresSuggestion { basic: boolean; note: string | null }
